import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactJson from 'react-json-view'
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Table,
  Badge,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { toast } from 'react-toastify';
import PaginationComponent from '../../Components/Common/Pagination';
import {
  requestPresignedUrl,
  uploadFileToS3,
  fetchInvoiceData,
  fetchUploadedInvoices,
  deleteInvoice,
  downloadInvoice
} from '../../slices/invoiceScanner/thunk';
import { resetUpload, clearError } from '../../slices/invoiceScanner/reducer';

const InvoiceTab = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  // Redux state using useSelector
  const {
    uploadedInvoices,
    totalCount,
    currentInvoice,
    invoiceData,
    preSignedData,
    loading,
    listLoading,
    invoiceLoading,
    deleteLoading,
    error
  } = useSelector((state) => state.Invoice);

  // Local state
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Set document title
  document.title = "Whistlemind Demo Engine | Invoice Scanner";

  // Clear any stale errors when component mounts
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Fetch uploaded invoices on component mount
  useEffect(() => {
    const offset = (currentPage - 1) * itemsPerPage;
    dispatch(fetchUploadedInvoices({ itemsPerPage, offset }));
  }, [dispatch, currentPage]);

  // Display error alerts (only show if not loading to avoid showing stale errors)
  useEffect(() => {
    if (error && !listLoading) {
      toast.error(error);
    }
  }, [error, listLoading]);

  // Toggle delete modal
  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    if (deleteModal) {
      setInvoiceToDelete(null);
    }
  };

  // Toggle view modal
  const toggleViewModal = () => {
    setViewModal(!viewModal);
    if (viewModal) {
      setSelectedInvoice(null);
    }
  };

  // Handle delete click
  const handleDeleteClick = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteModal(true);
  };


  const confirmDelete = async () => {
    if (invoiceToDelete) {
      try {
        await dispatch(deleteInvoice({ invoiceId: invoiceToDelete.document_id })).unwrap();
        toast.success(`Invoice "${invoiceToDelete.invoice_number || invoiceToDelete.file_name}" deleted successfully`);

        // Check if this was the last item on the current page
        const remainingItemsOnPage = uploadedInvoices.length - 1;

        // If no items remain on current page and we're not on page 1, go to previous page
        if (remainingItemsOnPage === 0 && currentPage > 1) {
          const newPage = currentPage - 1;
          setCurrentPage(newPage);
          const offset = (newPage - 1) * itemsPerPage;
          dispatch(fetchUploadedInvoices({ itemsPerPage, offset }));
        } else {
          // Otherwise, just refresh the current page
          const offset = (currentPage - 1) * itemsPerPage;
          dispatch(fetchUploadedInvoices({ itemsPerPage, offset }));
        }

        toggleDeleteModal();
      } catch (error) {
        toast.error('Failed to delete invoice. Please try again.');
      }
    }
  };

  // Handle upload button click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection and upload
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff'];
    if (!allowedTypes.includes(file.type)) {
      toast.warning('Please select a PDF or image file');
      event.target.value = '';
      return;
    }

    // Validate file size (e.g., max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.warning('File size must be less than 10MB');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    dispatch(resetUpload());

    try {
      // Step 1: Request pre-signed URL
      toast.info(`Uploading "${file.name}"...`);

      const urlResult = await dispatch(
        requestPresignedUrl({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size
        })
      ).unwrap();

      // Step 2: Upload to S3
      await dispatch(
        uploadFileToS3({
          file,
          preSignedUrl: urlResult.preSignedUrl
        })
      ).unwrap();

      toast.success('Invoice uploaded successfully! Processing...');

      // Refresh invoice list
      const offset = (currentPage - 1) * itemsPerPage;
      dispatch(fetchUploadedInvoices({ itemsPerPage, offset }));

    } catch (error) {
      const errorMessage = error || 'Upload failed. Please try again.';
      toast.error(errorMessage);
    }

    // Reset file input
    event.target.value = '';
  };

  // Handle invoice download
  const handleDownloadInvoice = async (invoiceId, fileName) => {
    try {
      toast.info('Preparing download...');

      await dispatch(downloadInvoice({ invoiceId, fileName })).unwrap();

      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  // Handle view invoice (fetch invoice data and open modal)
  const handleViewInvoice = async (invoice) => {
    if (invoice.correlation_id || invoice.document_id) {
      setSelectedInvoice(invoice);
      setViewModal(true);

      try {
        await dispatch(
          fetchInvoiceData({ correlationId: invoice.correlation_id || invoice.document_id })
        ).unwrap();
      } catch (error) {
        toast.error('Failed to fetch invoice data');
      }
    } else {
      toast.warning('No correlation ID found for this invoice');
    }
  };

  // Copy JSON data to clipboard
  const handleCopyJSON = () => {
    if (invoiceData?.invoiceData) {
      const mergedData = MergeFields(invoiceData.invoiceData);
      const jsonString = JSON.stringify(mergedData, null, 2);
      navigator.clipboard.writeText(jsonString);
      toast.success('Invoice data copied to clipboard!');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending_upload: 'warning',
      processing: 'info',
      completed: 'success',
      failed: 'danger',
    };
    return <Badge color={colors[status] || 'secondary'}>{getStatusMessage(status)}</Badge>;
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'pending_upload':
        return 'Pending';
      case 'completed':
        return 'Completed';
      case 'processing':
        return 'Processing';
      case "failed":
        return 'Failed';
      default:
        return 'Unknown status';
    }
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const offset = (pageNumber - 1) * itemsPerPage;
    dispatch(fetchUploadedInvoices({ itemsPerPage, offset }));
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const MergeFields = (data) => {
    if (!data) return null;

    return {
      ...data.fields,
      line_items: data.line_items
    };
  };

  const detIStTimemp = (dateStr) => {
    const utcDate = new Date(dateStr);

    // Convert to IST (UTC +5:30)
    const istOffset = 5.5 * 60 * 60 * 1000; // milliseconds
    const istDate = new Date(utcDate.getTime() + istOffset);

    // Format the output
    const formatted = istDate.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour12: false
    });
    return formatted;
  }

  return (
    <React.Fragment>
      <div>
        <Container fluid>
          {/* Main Content Area */}
          <Row>
            <Col>
              {/* Upload Button */}
              <div className="d-flex gap-2 mb-3b-3">
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleUploadClick}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" className="text-center" /> : <> <i className="ri-upload-line me-2"></i> Upload Invoice</>}

                  {/* {loading ? 'Uploading...' : ''} */}
                </Button>
                {/* Refresh Button */}
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    const offset = (currentPage - 1) * itemsPerPage;
                    dispatch(fetchUploadedInvoices({ itemsPerPage, offset }));
                    // toast.info('Refreshing file list...');
                  }}
                  disabled={listLoading || loading}
                >

                  <>
                    <i className="ri-refresh-line me-2"></i> Refresh
                  </>

                </Button>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.tiff"
                  style={{ display: 'none' }}
                />
              </div>
            </Col>
          </Row>

          <Row>
            {/* Invoice List */}
            <Col xl={12}>
              {/* Invoice List Card */}
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Invoice List</h5>
                </CardHeader>
                <CardBody>
                  {/* Show loading spinner when fetching data */}
                  {listLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading invoices...</p>
                    </div>
                  ) : uploadedInvoices && uploadedInvoices.length > 0 ? (
                    <>
                      {/* Table */}
                      <div className="table-responsive">
                        <Table className="table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>File Name</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadedInvoices.map((invoice) => (
                              <tr key={invoice.document_id}>
                                <td>
                                  <div>
                                    <i className="ri-file-text-line text-primary me-2"></i>
                                    <span className="fw-medium">{invoice.file_name || invoice.name || 'N/A'}</span>
                                  </div>
                                  <small className="text-muted">
                                    {detIStTimemp(invoice.updated_at || invoice.upload_date || '')}
                                    {/* {invoice.updated_at || invoice.upload_date || ''} */}
                                  </small>
                                </td>
                                <td>{getStatusBadge(invoice.status || 'Pending')}</td>
                                <td>
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      href="#"
                                      className="btn btn-soft-secondary btn-sm dropdown"
                                      tag="button"
                                    >
                                      <i className="ri-more-fill align-middle"></i>
                                    </DropdownToggle>
                                    <DropdownMenu
                                      className="dropdown-menu-end"
                                      container="body"
                                      style={{ zIndex: 1050 }}
                                    >
                                      <DropdownItem
                                        onClick={() => handleViewInvoice(invoice)}
                                        disabled={invoice.status === 'processing' || invoice.status === 'pending_upload'}
                                      >
                                        <i className="ri-eye-line me-2"></i>
                                        View
                                      </DropdownItem>
                                      {/* <DropdownItem
                                        onClick={() => handleDownloadInvoice(invoice.document_id, invoice.file_name)}
                                        disabled={invoice.status === 'processing' || invoice.status === 'pending_upload'}
                                      >
                                        <i className="ri-download-line me-2"></i>
                                        Download
                                      </DropdownItem> */}
                                      <DropdownItem divider />
                                      <DropdownItem
                                        onClick={() => handleDeleteClick(invoice)}
                                        className="text-danger"
                                      >
                                        <i className="ri-delete-bin-line me-2"></i>
                                        Delete
                                      </DropdownItem>
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {/* Pagination Info */}
                      <Row className="mt-3">
                        <Col>
                          <p className="text-muted mb-0">
                            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                            {Math.min(currentPage * itemsPerPage, totalCount)} of{' '}
                            {totalCount} entries
                          </p>
                        </Col>
                      </Row>

                      {/* Pagination - Only show if there are multiple pages */}
                      {totalPages > 1 && (
                        <PaginationComponent
                          currentPage={currentPage}
                          totalPages={totalPages}
                          handlePageChange={handlePageChange}
                        />
                      )}
                    </>
                  ) : (
                    /* Empty State */
                    <div className="text-center py-5">
                      <i
                        className="ri-file-pdf-2-line text-muted"
                        style={{ fontSize: '3rem' }}
                      ></i>
                      <p className="text-muted mt-3 mb-1">No invoices uploaded yet</p>
                      <p className="text-muted small">
                        Upload an invoice file to see it listed here
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Invoice View Modal */}
      <Modal
        isOpen={viewModal}
        toggle={toggleViewModal}
        style={{ maxWidth: '90%', width: '90%' }}
        centered
        backdrop="static"
      >
        <ModalHeader toggle={toggleViewModal}>
          <div className="d-flex align-items-center gap-2">
            <i className="ri-file-text-line"></i>
            Invoice Details - {selectedInvoice?.file_name || 'N/A'}
          </div>
        </ModalHeader>
        <ModalBody style={{ minHeight: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
          {invoiceLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="text-muted mt-3">Loading invoice data...</p>
            </div>
          ) : (
            <Row className="g-0">
              {/* Left Side: Invoice Image */}
              <Col md={6} className="pe-3 border-end">
                <div className="d-flex flex-column h-100">
                  <h6 className="mb-3 text-muted">Invoice Preview</h6>
                  <div className="flex-grow-1 d-flex align-items-center justify-content-center bg-light rounded p-3" style={{ minHeight: '500px' }}>
                    {invoiceData?.invoiceData?.s3_invoice_url ? (
                      invoiceData?.invoiceData?.file_name?.toLowerCase().endsWith('.pdf') ? (
                        <embed
                          src={invoiceData.invoiceData.s3_invoice_url}
                          type="application/pdf"
                          width="100%"
                          height="500px"
                          style={{ border: 'none', borderRadius: '4px' }}
                        />
                      ) : (
                        <img
                          src={invoiceData.invoiceData.s3_invoice_url}
                          alt="Invoice"
                          className="img-fluid rounded"
                          style={{ maxHeight: '500px', objectFit: 'contain' }}
                        />
                      )
                    ) : (
                      <div className="text-center">
                        <i className="ri-file-line text-muted" style={{ fontSize: '4rem' }}></i>
                        <p className="text-muted mt-3">Invoice preview not available</p>
                      </div>
                    )}
                  </div>
                </div>
              </Col>

              {/* Right Side: Extracted JSON Data */}
              <Col md={6} className="ps-3">
                <div className="d-flex flex-column h-100">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 text-muted">Extracted Data (JSON)</h6>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleCopyJSON}
                      disabled={!invoiceData?.invoiceData}
                    >
                      <i className="ri-file-copy-line me-1"></i>
                      Copy JSON
                    </Button>
                  </div>
                  <div
                    className="flex-grow-1 rounded p-3"
                    style={{
                      minHeight: '500px',
                      maxHeight: '500px',
                      overflowY: 'auto',
                      backgroundColor: '#1e1e1e'
                    }}
                  >
                    {invoiceData?.invoiceData ? (
                      <ReactJson
                        src={MergeFields(invoiceData.invoiceData)}
                        theme="monokai"
                        iconStyle="circle"
                        displayDataTypes={false}
                        displayObjectSize={false}
                        enableClipboard={false}
                        collapsed={false}
                        collapseStringsAfterLength={50}
                        style={{
                          fontFamily: 'Consolas, Monaco, monospace',
                          fontSize: '0.85rem',
                          backgroundColor: 'transparent',
                          padding: 0
                        }}
                      />
                    ) : (
                      <div className="text-center py-5">
                        <i className="ri-code-line" style={{ fontSize: '3rem', color: '#666' }}></i>
                        <p className="mt-3" style={{ color: '#999' }}>No extracted data available</p>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered backdrop="static">
        <ModalHeader toggle={toggleDeleteModal}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <i className="ri-delete-bin-line text-danger" style={{ fontSize: '3.5rem' }}></i>
            <h5 className="mt-3 mb-2">Are you sure?</h5>
            <p className="text-muted mb-1">
              Do you want to delete this invoice?
            </p>
            {invoiceToDelete && (
              <div className="mt-3 p-3 bg-light rounded">
                <div className="d-flex align-items-center justify-content-center">
                  <i className="ri-file-pdf-line text-danger me-2"></i>
                  <strong>{invoiceToDelete.file_name}</strong>
                </div>
              </div>
            )}
            <p className="text-muted mt-3 mb-0">
              <small>This action cannot be undone.</small>
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal} disabled={deleteLoading}>
            <i className="ri-close-line me-1"></i>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDelete} disabled={deleteLoading}>
            <i className="ri-delete-bin-line me-1"></i>
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default InvoiceTab;

